import { formatCurrency as fmtCurrency } from "utils/currency";
import {
  DeckCard,
  DeckCardData,
  DeckCardSortOptions,
  DeckDetails,
  DeckLocationEnum,
} from "modules/decks/model/deck";
import { SortOrderEnum } from "modules/collections/details/model/collection";
import {
  DeckLocationNameMap,
  ManaColorChartColorMapping,
  ManaColorNameMap,
} from "utils/constants";
import type { GameId } from "modules/common/model/models";
import {
  AutocompleteGameCard,
  CardPrinting,
  CommonPagedCard,
  FinishEnum,
  GameCard,
  MagicCard,
  ManaColorEnum,
  NONFOIL_FINISH,
} from "modules/cards/model/card";
import _groupBy from "lodash/groupBy";
import { CardGrouping } from "modules/common/model/models";
import { SubmittedDeckItem } from "modules/metagame/model/submitted-decks";
import { CollectionCard } from "modules/collections/details/model/collection";
import { TradeItem } from "modules/tradeLists/model/tradeList";
import { CardSet } from "modules/cards/model/set";
import { EmbeddedCardPrice } from "modules/cards/model/card-price";
import { finishSpec } from "modules/cards/model/finishBehavior";
import { getDeckGameAdapter } from "modules/common/gameRegistry";

export {
  capitalizeFirstLetter,
  cn,
  containsHtmlTags,
  costPrintedToManaFontClasses,
  firstUpper,
  getPokemonSetIconUrl,
  isVoidElement,
  manaSymbolsPairs,
  planesWalkerPairs,
  VOID_HTML_ELEMENTS,
} from "utils/presentation";

type CardLike = { id?: string; dataId?: string };

interface CardLinkable {
  game: string;
  setCode: string;
  slug: string;
  number: string;
  printing?: Pick<CardPrinting, "key">;
  printingKey?: string;
}

export function getCardUrl(card: CardLinkable, finish?: FinishEnum): string {
  const printingKey = card.printing?.key ?? card.printingKey;
  const printingPath = printingKey ? `/${encodeURIComponent(printingKey)}` : "";
  const params = new URLSearchParams();
  if (finish && finish !== NONFOIL_FINISH) params.set("finish", finish);
  const query = params.toString();
  return `/sets/${card.game}/${card.setCode.toLowerCase()}/${card.slug}/${card.number}${printingPath}${query ? `?${query}` : ""}`;
}

/**
 * Shared view-transition name pairing a card image with the card page hero,
 * so navigating between them morphs the image. Must be a page-unique CSS
 * custom-ident: only assign it to one element per card per page (never inside
 * the card details dialog — its content stays mounted while hidden and would
 * duplicate the page's name, aborting the transition).
 */
export function getCardHeroTransitionName(card: {
  game: string;
  id: string;
}): string {
  return heroTransitionName("card-hero", card.game, card.id);
}

/** Pairs a deck list tile with the deck details hero across navigations. */
export function getDeckHeroTransitionName(deck: {
  game: string;
  id: string;
}): string {
  return heroTransitionName("deck-hero", deck.game, deck.id);
}

/** Pairs a set row icon with the set page's header icon across navigations. */
export function getSetHeroTransitionName(set: {
  game: string;
  code: string;
}): string {
  return heroTransitionName("set-hero", set.game, set.code);
}

function heroTransitionName(prefix: string, ...parts: string[]): string {
  return [prefix, ...parts].join("-").replace(/[^a-zA-Z0-9_-]/g, "_");
}

export const getInternalCardId = (card: CardLike): string | null => {
  if ("dataId" in card && card.dataId) {
    return card.dataId;
  }
  if ("id" in card && card.id) {
    return card.id;
  }
  return null;
};

export const getCardImageUrl = (
  card: CardLike,
  isBack: boolean = false
): string => {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  const cardId = getInternalCardId(card);
  if (!cardId) return "";
  return `${cdnBase}/card/${cardId}${isBack ? "/back" : ""}`;
};

export const getCardArtUrl = (card: CardLike): string => {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  const cardId = getInternalCardId(card);
  if (!cardId) return "";
  return `${cdnBase}/art/${cardId}?v=7`;
};

export function getSealedProductImageUrl(
  productId: string | undefined
): string | null {
  if (!productId) return null;
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return null;
  return `${cdnBase}/sealed/${productId}`;
}

export const getOccurrences = <K>(items: K[]) => {
  const counts = new Map<K, number>();

  items.forEach((c) => {
    if (!counts.has(c)) {
      counts.set(c, 1);
    } else {
      let val = counts.get(c) as number;
      val++;
      counts.set(c, val);
    }
  });

  return counts;
};

export const repeat = (array: any[], n: number): any[] => {
  let out: any[] = [];
  for (let i = 0; i < n; i++) {
    out = out.concat(array);
  }
  return out;
};

export const deckManaDistributionData = (cards: DeckCard[]): any[] => {
  const allColors = cards
    .map((dc) => {
      if (!dc.colors.length) {
        return repeat([ManaColorEnum.Colorless], dc.quantity);
      }
      return repeat(dc.colors, dc.quantity);
    })
    .flat();

  const colorMapping = ManaColorChartColorMapping();
  const occurrences = getOccurrences<ManaColorEnum>(allColors);
  const data: any[] = [];
  occurrences.forEach((v, k) => {
    data.push({
      value: occurrences.get(k),
      name: ManaColorNameMap[k],
      raw: k,
      itemStyle: {
        color: colorMapping.get(k),
      },
    });
  });
  return data;
};

export const deckTypesDistributionData = (cards: DeckCard[]): any[] => {
  const allTypes = cards.map((dc) => repeat(dc.types, dc.quantity)).flat();
  const occurrences = getOccurrences<string>(allTypes);
  const data: any[] = [];
  occurrences.forEach((v, k) => {
    data.push({
      value: occurrences.get(k),
      raw: k,
      name: k,
    });
  });
  return data;
};

export const deckLandDistributionData = (cards: DeckCard[]): any[] => {
  const landColors = cards
    .map((dc) => {
      if (!dc.colorIdentity.length) {
        return repeat([ManaColorEnum.Colorless], dc.quantity);
      }
      return repeat(dc.colorIdentity, dc.quantity);
    })
    .flat();

  const colorMapping = ManaColorChartColorMapping();
  const data: any[] = [];
  const occurrences = getOccurrences<ManaColorEnum>(landColors);
  occurrences.forEach((v, k) => {
    data.push({
      value: occurrences.get(k),
      name: ManaColorNameMap[k],
      raw: k,
      itemStyle: {
        color: colorMapping.get(k),
      },
    });
  });
  return data;
};

export const deckManaValueDistributionData = (cards: DeckCard[]): any[] => {
  const uniqManaValues = uniqueManaValues(cards.map((dc) => dc));

  const allManaValues = cards
    .map((dc) => repeat([dc.costValue], dc.quantity))
    .flat();
  const occurrences = getOccurrences<number>(allManaValues);

  const data: any[] = [];
  Array.from(uniqManaValues)
    .sort((a, b) => a - b)
    .forEach((v) => {
      data.push({
        value: occurrences.get(v),
        name: v.toString(),
        raw: v,
      });
    });

  return data;
};

export const uniqueManaValues = (cards: DeckCardData[]): Set<number> => {
  return cards
    .filter(
      (c) =>
        !c.types.includes("Legend") &&
        !c.types.includes("Land") &&
        !c.types.includes("Rune") &&
        !c.types.includes("Battlefield")
    )
    .map((c) => c.costValue)
    .flat(1)
    .reduce((acc, curr) => {
      acc.add(curr);
      return acc;
    }, new Set<number>());
};

export const deckManaValueDistributionXAxis = (cards: DeckCard[]): any[] => {
  return Array.from(uniqueManaValues(cards)).sort((a, b) => a - b);
};

export const deckManaIdentity = (cards: DeckCard[]): string[] => {
  const cardColors = cards
    .map((dc) =>
      dc.colors.map((ci) => ci.toLowerCase() || ManaColorEnum.Colorless)
    )
    .flat();
  const set = new Set(cardColors);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

export enum ManaColorCombination {
  B = "b",
  U = "u",
  G = "g",
  R = "r",
  W = "w",
  UW = "uw",
  BW = "bw",
  BU = "bu",
  RU = "ru",
  BG = "bg",
  BR = "br",
  RW = "rw",
  GR = "gr",
  GU = "gu",
  GW = "gw",
  GUW = "guw",
  BUW = "buw",
  BRU = "bru",
  BGR = "bgr",
  GRW = "grw",
  BGW = "bgw",
  RUW = "ruw",
  BGU = "bgu",
  BRW = "brw",
  GRU = "gru",
  BGRU = "bgru",
  BGRW = "bgrw",
  GRUW = "gruw",
  BGUW = "bguw",
  BRUW = "bruw",
  BGRUW = "bgruw",
}

const manaColorsToManaFontIndicator = new Map<ManaColorCombination, string>([
  [ManaColorCombination.B, "b"],
  [ManaColorCombination.U, "u"],
  [ManaColorCombination.G, "g"],
  [ManaColorCombination.R, "r"],
  [ManaColorCombination.W, "w"],
  [ManaColorCombination.UW, "wu"],
  [ManaColorCombination.BW, "wb"],
  [ManaColorCombination.BU, "ub"],
  [ManaColorCombination.RU, "ur"],
  [ManaColorCombination.BG, "bg"],
  [ManaColorCombination.BR, "br"],
  [ManaColorCombination.RW, "rw"],
  [ManaColorCombination.GR, "rg"],
  [ManaColorCombination.GU, "gu"],
  [ManaColorCombination.GW, "gw"],
  [ManaColorCombination.GUW, "wug"],
  [ManaColorCombination.BUW, "ubw"],
  [ManaColorCombination.BRU, "bru"],
  [ManaColorCombination.BGR, "rgb"],
  [ManaColorCombination.GRW, "gwr"],
  [ManaColorCombination.BGW, "wbg"],
  [ManaColorCombination.RUW, "urw"],
  [ManaColorCombination.BGU, "bgu"],
  [ManaColorCombination.BRW, "rwb"],
  [ManaColorCombination.GRU, "gur"],
  [ManaColorCombination.BGRU, "ubrg"],
  [ManaColorCombination.BGRW, "brgw"],
  [ManaColorCombination.GRUW, "rgwu"],
  [ManaColorCombination.BGUW, "gwub"],
  [ManaColorCombination.BRUW, "wubr"],
  [ManaColorCombination.BGRUW, "5"],
]);

export const manaColorsToNames = new Map<ManaColorCombination, string>([
  [ManaColorCombination.B, "Black"],
  [ManaColorCombination.U, "Blue"],
  [ManaColorCombination.G, "Green"],
  [ManaColorCombination.R, "Red"],
  [ManaColorCombination.W, "White"],
  [ManaColorCombination.UW, "Azorius"],
  [ManaColorCombination.BW, "Orzhov"],
  [ManaColorCombination.BU, "Dimir"],
  [ManaColorCombination.RU, "Izzet"],
  [ManaColorCombination.BG, "Golgari"],
  [ManaColorCombination.BR, "Rakdos"],
  [ManaColorCombination.RW, "Boros"],
  [ManaColorCombination.GR, "Gruul"],
  [ManaColorCombination.GU, "Simic"],
  [ManaColorCombination.GW, "Selesnya"],
  [ManaColorCombination.GUW, "Bant"],
  [ManaColorCombination.BUW, "Esper"],
  [ManaColorCombination.BRU, "Grixis"],
  [ManaColorCombination.BGR, "Jund"],
  [ManaColorCombination.GRW, "Naya"],
  [ManaColorCombination.BGW, "Abzan"],
  [ManaColorCombination.RUW, "Jeskai"],
  [ManaColorCombination.BGU, "Sultai"],
  [ManaColorCombination.BRW, "Mardu"],
  [ManaColorCombination.GRU, "Temur"],
  [ManaColorCombination.BGRU, "Glint"],
  [ManaColorCombination.BGRW, "Dune"],
  [ManaColorCombination.GRUW, "Ink"],
  [ManaColorCombination.BGUW, "Witch"],
  [ManaColorCombination.BRUW, "Yore"],
  [ManaColorCombination.BGRUW, "Rainbow"],
]);
export const sagaPairs = {
  I: "1",
  II: "2",
  III: "3",
  IV: "4",
  V: "5",
  VI: "6",
  VII: "7",
  VIII: "8",
  IX: "9",
  X: "10",
};
export const toManaFontColorIndicator = (
  colors: ManaColorCombination
): string => {
  return manaColorsToManaFontIndicator.get(colors) as string;
};

export const mainboardGroupingToManaFontClasses = (group: string): string[] => {
  const result: string[] = [];

  const matches = group.split(",");
  if (matches) {
    matches.forEach((m) => {
      // if it's a split mana cost, we need to handle it as such
      if (m.includes("/")) {
        const noSlash = m.replace("/", "");
        result.push(noSlash.toLocaleLowerCase());
      } else {
        result.push(m.toLocaleLowerCase());
      }
    });
  }

  if (!matches.length) {
    matches.push(ManaColorEnum.Colorless.toLocaleLowerCase());
  }

  return result;
};

// Both deck totals value each row at its own finish (getFinishValue), so foil/
// etched rows reflect their real price instead of the nonfoil default and stay
// consistent with the StatsRibbon total.
export const totalDeckPrice = (cards: DeckCard[]): number =>
  cards.reduce(
    (sum, dc) => sum + getFinishValue(dc.price, dc.finish) * dc.quantity,
    0
  );

export function calculateGroupTotalPrice(cards: DeckCard[]): number {
  return cards.reduce(
    (total, card) =>
      total + getFinishValue(card.price, card.finish) * card.quantity,
    0
  );
}

export const shuffleArray = (arr: any[], count: number = 10) => {
  for (let x = 0; x < count; x++) {
    for (let i = arr.length - 1; i > 0; i--) {
      let j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
};

export const printDeck = (deck: DeckDetails | SubmittedDeckItem) => {
  const locationGroups = _groupBy(
    deck.cards,
    (dc: DeckCard) => DeckLocationNameMap[dc.location]
  );

  const deckList = Object.entries<DeckCard[]>(locationGroups).map(
    ([location, cards]: [string, DeckCard[]]) => {
      return `${location}\n${cards.reduce((prev, curr) => {
        return prev + `${curr.quantity} ${curr.name}\n`;
      }, "")}`;
    }
  );

  const element = document.createElement("a");
  const file = new Blob([deckList.join("\n")], {
    type: "text/plain",
  });
  element.href = URL.createObjectURL(file);
  if ("name" in deck) {
    element.download = `${deck.name}.txt`;
  } else {
    element.download = `${deck.archetypeSlug}.txt`;
  }
  document.body.appendChild(element);
  element.click();
};

/** Past this, a zero-trade account is no longer "new" — it just hasn't traded,
 *  and calling it new next to "Member since 2022" contradicts itself. */
const NEW_TRADER_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

/** Whether an account is young enough that "New trader" beats showing no trades. */
export const isNewTrader = (createdAt?: string) =>
  !!createdAt &&
  Date.now() - new Date(createdAt).getTime() < NEW_TRADER_MAX_AGE_MS;

/** The one shape "Aug 31, 25" is written in, shared by both variants below. */
const SHORT_DATE: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "short",
  day: "numeric",
};

/**
 * Date-only values — set release dates, tournament dates — arrive as UTC
 * midnight and carry no time of day, so the zone is pinned: rendering a
 * September 1st release as "Aug 31" west of UTC is wrong, not just a hydration
 * mismatch. Pinned means server and browser agree by construction, so this is
 * also the only date formatter safe to call from a Server Component.
 *
 * For a real instant (createdAt, updatedAt, …) use `toLocalDateFormat` inside
 * `<LocalTime>` instead.
 */
export const toDateFormat = (dateString: string | Date) => {
  return new Date(dateString).toLocaleDateString("en", {
    ...SHORT_DATE,
    timeZone: "UTC",
  });
};

/**
 * `toDateFormat`'s shape in the viewer's own zone, for real instants. Only
 * valid inside `<LocalTime>` — called directly it renders UTC on the server and
 * local in the browser (hydration error #418).
 */
export const toLocalDateFormat = (date: Date) =>
  date.toLocaleDateString("en", SHORT_DATE);

/**
 * Date + time without seconds ("Sep 2, 2026, 14:03") in the viewer's zone,
 * for scheduled instants (meeting times, quote expiries). 24h like the rest
 * of the app's clock times. Only valid inside `<LocalTime>`.
 */
export const toLocalDateTimeFormat = (date: Date) =>
  date.toLocaleString("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

/** Date + time in the viewer's zone. Only valid inside `<LocalTime>`. */
export const toDateFormatExtended = (dateString: string | Date) => {
  return new Date(dateString).toLocaleDateString("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  });
};

/**
 * Date + time in the standard European format (DD/MM/YYYY, 24h) via the
 * `en-GB` locale. The locale is pinned so the digit/format output is stable,
 * but the runtime timezone still differs between server (UTC) and the browser
 * (local), so this is only valid inside `<LocalTime>`.
 *
 * `suppressHydrationWarning` is not an alternative: React accepts the server's
 * text without patching it, and every later render produces the same local
 * string, so the no-op diff leaves the UTC rendering on screen forever.
 */
export const toEuDateFormatExtended = (dateString: string | Date) => {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export const containsText = (text: string, searchText: string) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

/**
 * Whether a nav route should render as active for the current pathname.
 * Matches the route itself and its sub-paths by full segment, so "/decks"
 * is active on "/decks/123" but NOT on "/x/decks" or "/decksomething".
 */
export const activeButton = (
  pathname: string | null,
  route: string | string[]
): boolean => {
  if (Array.isArray(route)) {
    return route.some((r) => activeButton(pathname, r));
  }
  if (!pathname) return false;
  if (route === "/") {
    return pathname === route;
  }
  return pathname === route || pathname.startsWith(`${route}/`);
};

export const getRandomIntInRange = (min: number, max: number): number => {
  const cmin = Math.ceil(min);
  const fmax = Math.floor(max);
  return Math.floor(Math.random() * (fmax - cmin) + cmin); // The maximum is exclusive and the minimum is inclusive
};

export const groupedCardsInLocation = (
  location: DeckLocationEnum,
  grouping: CardGrouping,
  data: DeckCard[],
  game?: GameId
) => {
  const cards = data.filter((dc) => dc.location === location);

  const isSpecialLocation = [
    DeckLocationEnum.Commander,
    DeckLocationEnum.Sideboard,
    DeckLocationEnum.Considering,
    DeckLocationEnum.Companion,
    DeckLocationEnum.Don,
  ].includes(location);

  if (isSpecialLocation) {
    if (location === DeckLocationEnum.Sideboard) {
      cards.unshift(
        ...data.filter((dc) => dc.location === DeckLocationEnum.Companion)
      );
    }
    // The CommanderLoc slot is renamed per game (Leader for OP,
    // Legend for Riftbound, Commander for MTG). Use locationLabel when
    // we know the game so the section heading reflects the right
    // marketing; fall back to the static map for legacy callers that
    // still pass cards without a game.
    const sectionLabel = game
      ? getDeckGameAdapter(game).locationLabel(location)
      : DeckLocationNameMap[location];
    const specialLocCards = _groupBy(cards, () => [sectionLabel]);

    return {
      ...specialLocCards,
    };
  }

  let legendGroup = {};
  if (cards.some((dc) => dc.types.includes("Legend"))) {
    legendGroup = _groupBy(
      cards.filter((dc) => dc.types.includes("Legend")),
      (c) => c.types
    );
  }

  let battlefieldGroup = {};
  if (cards.some((dc) => dc.types.includes("Battlefield"))) {
    battlefieldGroup = _groupBy(
      cards.filter((dc) => dc.types.includes("Battlefield")),
      (c) => c.types
    );
  }

  const lands = cards.filter(
    (c) => c.types.includes("Land") || c.types.includes("Rune")
  );
  const landTypeGroup = _groupBy(lands, (c) => c.types.join(", "));
  const cardsNotLands = cards.filter(
    (c) =>
      !c.types.includes("Land") &&
      !c.types.includes("Rune") &&
      !c.types.includes("Battlefield")
  );
  const sortedByType = cardsNotLands
    .slice()
    .sort((a, b) => a.types[0].localeCompare(b.types[0]));

  switch (grouping) {
    case CardGrouping.FirstType:
      return {
        ...legendGroup,
        ...battlefieldGroup,
        ..._groupBy(sortedByType, (c) => c.types),
        ...landTypeGroup,
      };

    case CardGrouping.Color:
      return {
        ...battlefieldGroup,
        ..._groupBy(sortedByType, (c) => c.colors || [ManaColorEnum.Colorless]),
        ...landTypeGroup,
      };
    case CardGrouping.ManaValue:
      return {
        ...legendGroup,
        ...battlefieldGroup,
        ..._groupBy(
          sortedByType.filter((dc) => !dc.types.includes("Legend")),
          (c) => c.costValue
        ),
        ...landTypeGroup,
      };

    case CardGrouping.ColorIdentity:
      return {
        ...battlefieldGroup,
        ..._groupBy(sortedByType, (c) =>
          c.colorIdentity?.length ? c.colorIdentity : [ManaColorEnum.Colorless]
        ),
        ...landTypeGroup,
      };

    case CardGrouping.None:
      return {
        Mainboard: cards,
      };
  }
};

export function sortDeckCards(
  cards: DeckCard[],
  sort: DeckCardSortOptions,
  order: SortOrderEnum
): DeckCard[] {
  const dir = order === SortOrderEnum.Descending ? -1 : 1;

  return [...cards].sort((a, b) => {
    switch (sort) {
      case DeckCardSortOptions.Name:
        return a.name.localeCompare(b.name) * dir;
      case DeckCardSortOptions.ManaValue:
        return (a.costValue - b.costValue) * dir;
      case DeckCardSortOptions.Price: {
        const priceA = getFinishValue(a.price, a.finish);
        const priceB = getFinishValue(b.price, b.finish);
        return (priceA - priceB) * dir;
      }
      default:
        return 0;
    }
  });
}

export const totalLocationCards = (
  location: DeckLocationEnum,
  cards: DeckCard[],
  excludeTypes?: readonly string[]
) => {
  return cards
    .filter((dc) => dc.location === location)
    .filter(
      (dc) =>
        !excludeTypes?.length || !dc.types.some((t) => excludeTypes.includes(t))
    )
    .reduce((total, b) => total + b.quantity, 0);
};

export const cardsInLocation = (
  location: DeckLocationEnum,
  cards: DeckCard[]
) => {
  return cards.filter((dc) => dc.location === location);
};

export function isUserDeck(
  deck: DeckDetails | SubmittedDeckItem
): deck is DeckDetails {
  return (deck as DeckDetails).user !== undefined;
}

// Utility function to truncate text
export const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
};

export const clerkImage = (
  preferredAvatar: string,
  height: number = 48,
  width: number = 48,
  quality: number = 100
): string => {
  const params = new URLSearchParams();
  params.set("height", `${height}`);
  params.set("width", `${width}`);
  params.set("quality", `${quality}`);
  params.set("fit", "scale-down");

  return `${preferredAvatar}?${params.toString()}`;
};

export function isCollectionCard(
  card: CollectionCard | TradeItem | GameCard | CardSet | DeckCardData
): card is CollectionCard {
  // CollectionCard has purchasePrice property (even if optional/undefined)
  // TradeItem has list property but no purchasePrice
  return "purchasePrice" in card && !("list" in card);
}

export function isTradeItem(
  card:
    | CollectionCard
    | TradeItem
    | GameCard
    | AutocompleteGameCard
    | CardSet
    | DeckCardData
): card is TradeItem {
  // TradeItem has list property with id, and may or may not have listId
  return (
    "list" in card &&
    typeof (card as any).list === "object" &&
    (card as any).list?.id
  );
}

export function isCardSet(item: CollectionCard | CardSet): item is CardSet {
  return "code" in item && "totalSetSize" in item && !("listId" in item);
}

export function isAutocompleteGameCard(
  item: CollectionCard | TradeItem | GameCard | AutocompleteGameCard | CardSet
): item is AutocompleteGameCard {
  return (
    "keyruneCode" in item &&
    "scryfallId" in item &&
    !("set" in item) &&
    !("listId" in item)
  );
}

export const formatMarketCap = (
  value: number | undefined,
  currency: string
) => {
  if (value === undefined) return "N/A";
  return `≈ ${fmtCurrency(value, currency)}`;
};

/**
 * Sorts an array of card printings by their set release date (newest first)
 *
 * @param printings Array of card printing objects with set.releaseDate
 * @returns A new sorted array with newest printings first
 */
export function sortPrintingsByReleaseDate<
  T extends { set: { releaseDate: string } },
>(printings: T[]): T[] {
  return [...printings].sort((a, b) => {
    // Parse the ISO date strings into Date objects
    const dateA = new Date(a.set.releaseDate);
    const dateB = new Date(b.set.releaseDate);

    // Sort descending (newest first)
    return dateB.getTime() - dateA.getTime();
  });
}

/**
 * Sorts an array of card printings by their set release date with option to control sort direction
 *
 * @param printings Array of card printing objects with set.releaseDate
 * @param ascending If true, sort oldest first; if false, sort newest first (default: false)
 * @returns A new sorted array
 */
export function sortPrintingsByReleaseDateWithDirection<
  T extends { set: { releaseDate: string } },
>(printings: T[], ascending: boolean = false): T[] {
  return [...printings].sort((a, b) => {
    // Parse the ISO date strings into Date objects
    const dateA = new Date(a.set.releaseDate);
    const dateB = new Date(b.set.releaseDate);

    // Sort based on direction parameter
    return ascending
      ? dateA.getTime() - dateB.getTime() // Ascending (oldest first)
      : dateB.getTime() - dateA.getTime(); // Descending (newest first)
  });
}

export function getCardPrice(
  card: TradeItem | CollectionCard | GameCard | DeckCardData | CommonPagedCard,
  isFoil: boolean = false
): string | null {
  return getSpecificPrice(card.price, isFoil);
}

export function getSpecificPrice(
  price: EmbeddedCardPrice | undefined,
  foil: boolean = false
): string {
  if (!price) return "";
  let priceValue = 0;

  if (foil) {
    priceValue = price.value.foil || price.value.default || 0;
  } else {
    priceValue = price.value.default || 0;
  }
  if (priceValue === 0) return "";
  return fmtCurrency(priceValue, price.currency);
}

/**
 * Honest per-finish price reader. Returns the formatted price for the
 * requested finish, or empty string when there's no real data for that
 * finish — no cross-finish fallback. Used by finish-aware UIs that need
 * to show "the foil price specifically" without `getSpecificPrice`'s
 * helpful-but-misleading `value.foil || value.default` substitution.
 *
 * Counterpart to `getCardPrice` (which keeps the any-price/fallback
 * semantics for callers that just want "a representative price").
 */
// Raw per-finish price field (0 when that finish has no chart data). The
// single source of the finish→field mapping. Adding a new finish is one
// entry here plus a field on the price model — getFinishPrice (display) and
// getFinishValue (summation) both build on this, so they never drift.
function rawFinishValue(
  price: EmbeddedCardPrice | undefined,
  finish: FinishEnum
): number {
  const key = finishSpec(finish).priceKey;
  return key ? (price?.value[key] ?? 0) : 0;
}

export function getFinishPrice(
  price: EmbeddedCardPrice | undefined,
  finish: FinishEnum
): string {
  const v = rawFinishValue(price, finish);
  return v > 0 ? fmtCurrency(v, price?.currency) : "";
}

/**
 * Numeric per-finish value for summing (deck/hand/collection totals). Same
 * field selection as getFinishPrice (via rawFinishValue), but returns a
 * number and falls back to the representative `value.default` when the row's
 * finish has no separate chart — so a foil row still counts at the default
 * rather than dropping to 0. This deliberate fallback is the one difference
 * from getFinishPrice, which stays blank for honest display; summation
 * prefers a best estimate over dropping a row's value. NonFoil resolves to
 * `default` whenever a real nonfoil chart exists (they're equal then), so
 * nonfoil totals are unchanged.
 */
export function getFinishValue(
  price: EmbeddedCardPrice | undefined,
  finish: FinishEnum
): number {
  return rawFinishValue(price, finish) || price?.value?.default || 0;
}

/**
 * Derived "is the displayed `value.default` actually an approximation?"
 * signal. A price is approximate only when the card *supports* a nonfoil
 * finish but `value.default` is mirrored from foil/metal/etched because we
 * don't have a real nonfoil chart for it. For foil-only or etched-only
 * cards, `value.default` reflects the card's only finish and isn't an
 * approximation — it's the legitimate primary.
 *
 * `price.source` is authoritative. Documents without it are unclassified;
 * consumers do not reconstruct a second source from coincident price values.
 */
export function isPriceApproximate(card: {
  price?: EmbeddedCardPrice;
  finishes: FinishEnum[];
}): { approximate: boolean; source: FinishEnum | null } {
  const p = card.price;
  const v = p?.value;
  if (!v || !v.default) return { approximate: false, source: null };

  // Preferred path: explicit `price.source` set by the updater. For
  // single-finish records (collection-cards, trade-items, single-print
  // master cards) the source is per-doc — if it doesn't match the doc's
  // finish, `value.default` was mirrored from a different finish chart
  // and is therefore approximate. For multi-finish master docs the
  // source identifies the chart that fed `value.default` and is treated
  // as "approximate" iff the card supports nonfoil but the source isn't
  // nonfoil (i.e. the user expected a nonfoil price and we substituted).
  const src = p.source as FinishEnum | undefined;
  if (src) {
    if (card.finishes.length === 1) {
      const own = card.finishes[0];
      if (src === own) return { approximate: false, source: null };
      return { approximate: true, source: src };
    }
    if (card.finishes.includes(NONFOIL_FINISH) && src !== NONFOIL_FINISH) {
      return { approximate: true, source: src };
    }
    return { approximate: false, source: null };
  }

  return { approximate: false, source: null };
}

/**
 * Safely gets the listId from a TradeItem, handling both API response formats
 * Some API responses have listId directly, others only have list.id
 */
export const getTradeItemListId = (item: TradeItem): string | null => {
  if (item.listId) {
    return item.listId;
  }
  if (item.list?.id) {
    return item.list.id;
  }
  return null;
};

/**
 * Ensures a website URL has a valid protocol, defaulting to HTTPS
 * @param website - The website URL string
 * @returns A valid URL with protocol or null if empty/invalid
 */
export const ensureValidWebsiteUrl = (
  website: string | undefined | null
): string | null => {
  if (!website || website.trim() === "") {
    return null;
  }

  const cleanWebsite = website.trim();

  // Check if it already has a protocol
  if (
    cleanWebsite.startsWith("http://") ||
    cleanWebsite.startsWith("https://")
  ) {
    return cleanWebsite;
  }

  // Add https:// protocol for URLs without protocol
  return `https://${cleanWebsite}`;
};

export function getOrigin(fullUrl: string): string | undefined {
  if (!fullUrl) return undefined;

  try {
    return new URL(fullUrl).origin;
  } catch {
    return undefined;
  }
}

export function getCardOgImageUrl(
  game: string,
  setCode: string,
  cardNumber: string,
  printingKey?: string
): string {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  // Encode each segment so OG meta tags emit a single, valid URL.
  // Set codes can include spaces (One Piece "op05 ann") and card numbers
  // can carry promo marks (★ / †); raw URLs in og:image trigger
  // double-encoding by some social-bot crawlers.
  const base = `${cdnBase}/og/card/${encodeURIComponent(game)}/${encodeURIComponent(setCode)}/${encodeURIComponent(cardNumber)}`;
  return printingKey
    ? `${base}?printing=${encodeURIComponent(printingKey)}`
    : base;
}

function appendOgImageQuery(url: string, query = ""): string {
  const normalizedQuery = query.startsWith("?") ? query.slice(1) : query;
  return normalizedQuery ? `${url}?${normalizedQuery}` : url;
}

export function getListOgImageUrl(listId: string, query = ""): string {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  return appendOgImageQuery(`${cdnBase}/og/list/${listId}`, query);
}

export function getDeckOgImageUrl(deckId: string): string {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  return `${cdnBase}/og/deck/${deckId}`;
}

export function getDropOgImageUrl(dropId: string): string {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  return `${cdnBase}/og/drop/${dropId}`;
}

export function getLgsLogoUrl(
  wotcId: number,
  variant?: "light" | "dark",
  small = false
): string {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  const filename = variant ? `logo_${variant}.webp` : "logo.webp";
  return `${cdnBase}/lgs/${wotcId}/${small ? "small/" : ""}${filename}`;
}

export function getStoreOgImageUrl(slug: string, query = ""): string {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  return appendOgImageQuery(`${cdnBase}/og/store/${slug}`, query);
}

export function getCityOgImageUrl(citySlug: string, query = ""): string {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  return appendOgImageQuery(`${cdnBase}/og/city/${citySlug}`, query);
}

export function getUserOgImageUrl(username: string): string {
  const cdnBase = process.env.NEXT_PUBLIC_IMAGE_CDN;
  if (!cdnBase) return "";
  return `${cdnBase}/og/user/${username}`;
}

/**
 * Builds the public URL path for a deck detail page.
 * Decks with a format are nested under it: /decks/mtg/commander/abc123
 * Decks without a format use the flat path: /decks/riftbound/abc123
 */
export function getPublicDeckPath(
  game: string,
  deckId: string,
  format?: string
): string {
  if (format) {
    return `/decks/${game}/${format}/${deckId}`;
  }
  return `/decks/${game}/${deckId}`;
}

/**
 * Build the canonical public URL for a trade list. The published list lives
 * under its owner's profile (so breadcrumbs read User > Lists > Name); the
 * legacy /lists/{id} route 308-redirects here.
 */
export function getPublicListPath(username: string, listId: string): string {
  return `/users/${username}/lists/${listId}`;
}
